import React, { useContext, useState } from 'react';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { SlideDown } from 'react-slidedown';
import 'react-slidedown/lib/slidedown.css';
import { useIntersectionObserver } from 'usehooks-ts';
import {
  HundredDayWarrantyContent,
  HundredDayWarrantyHeader,
} from '../_common/hundred-day-warranty/HundredDayWarranty';
import {
  ManufacturerWarrantyContent,
  ManufacturerWarrantyHeader,
} from '../_common/manufacturer-warranty/ManufacturerWarranty';
import {
  HeaderButton,
  HeaderIcon,
  HR,
  WarrantiesCardContentContainer,
  WarrantiesCardHeaderContainer,
  WarrantiesCardWrapper,
  WarrantiesHeader,
} from './WarrantiesCard.styles';
import ManufacturersWarrantyIcon from '../../assets/ManufacturersWarrantyIcon';
import { AnalyticEvents } from '../../constants';

const Intersector = ({ children, captureImpression, customAttributes }: any) => {
  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (isIntersecting && captureImpression) captureImpression(customAttributes);
    },
  });

  return <div ref={ref}>{children}</div>;
};

const WarrantiesCard = ({ feedData, positionValue, userState }: any) => {
  const { trackEvent } = useContext(AnalyticsContext) as any;
  const [cardPosition] = useState(positionValue || 0);
  const {
    id: cardId,
    title,
    content: { limitedWarranty, manufacturerWarranty },
  } = feedData;

  const {
    vehicle: { make },
  } = userState;

  const [basicWarranty, powertrainWarranty] = manufacturerWarranty || [];

  const [isManufacturersCardOpen, setIsManufacturersCardOpen] = useState(false);
  const [isHundredDayCardOpen, setIsHundredDayCardOpen] = useState(false);

  const captureImpression = (customAttributes: any) => {
    setTimeout(() => {
      trackEvent(AnalyticEvents.viewedOvpCardImpression, {
        cardId,
        cardPosition,
        ...customAttributes,
      });
      datadogRum.addAction(AnalyticEvents.viewedOvpCardImpression, {
        cardId,
        cardPosition,
        ...customAttributes,
      });
    }, 1000);
  };

  const trackEventHandler = (isCardOpen: boolean, type: string) => {
    trackEvent(isCardOpen ? AnalyticEvents.ovpCardExpand : AnalyticEvents.ovpCardCollapse, {
      cardId,
      cardPosition,
      type,
    });
    datadogRum.addAction(isCardOpen ? AnalyticEvents.ovpCardExpand : AnalyticEvents.ovpCardCollapse, {
      cardId,
      cardPosition,
      type,
    });
  };

  const handle100DayWarrantyCardClick = (isCardOpen: boolean) => {
    trackEventHandler(isCardOpen, '100_day_warranty');
    setIsHundredDayCardOpen(!isHundredDayCardOpen);
  };

  const handleManufacturersCardClick = (isCardOpen: boolean) => {
    trackEventHandler(isCardOpen, 'manufacturer_warranty');
    setIsManufacturersCardOpen(!isManufacturersCardOpen);
  };

  if (!limitedWarranty && (!manufacturerWarranty || manufacturerWarranty?.length === 0)) {
    return null;
  }

  return (
    <WarrantiesCardWrapper>
      <WarrantiesCardHeaderContainer>
        <HeaderIcon data-testid="warranties-card-title-icon">
          <ManufacturersWarrantyIcon />
        </HeaderIcon>
        <WarrantiesHeader data-testid="warranties-card-title">{title}</WarrantiesHeader>
      </WarrantiesCardHeaderContainer>
      <WarrantiesCardContentContainer>
        {limitedWarranty && (
          <Intersector
            customAttributes={{
              type: [limitedWarranty?.type?.toLowerCase()],
              isExpired: [limitedWarranty?.isExpired],
            }}
            captureImpression={captureImpression}
          >
            <>
              <HeaderButton
                onClick={() => {
                  handle100DayWarrantyCardClick(!isHundredDayCardOpen);
                }}
                data-testid="toggle-hundred-day-card"
              >
                <HundredDayWarrantyHeader isCardOpen={isHundredDayCardOpen} limited={limitedWarranty} />
              </HeaderButton>
              <SlideDown>
                {isHundredDayCardOpen && (
                  <HundredDayWarrantyContent limited={limitedWarranty} cardId={cardId} cardPosition={cardPosition} />
                )}
              </SlideDown>
            </>
          </Intersector>
        )}
        {manufacturerWarranty?.length > 0 && limitedWarranty && <HR />}
        {manufacturerWarranty?.length > 0 && basicWarranty && powertrainWarranty && (
          <Intersector
            customAttributes={{
              type: [basicWarranty?.type?.toLowerCase(), powertrainWarranty?.type?.toLowerCase()],
              isExpired: [basicWarranty?.isExpired, powertrainWarranty?.isExpired],
            }}
            captureImpression={captureImpression}
          >
            <>
              <HeaderButton
                onClick={() => {
                  handleManufacturersCardClick(!isManufacturersCardOpen);
                }}
                data-testid="toggle-manufacturers-card"
              >
                <ManufacturerWarrantyHeader
                  isCardOpen={isManufacturersCardOpen}
                  manufacturer={manufacturerWarranty}
                  captureImpression={captureImpression}
                />
              </HeaderButton>
              <SlideDown>
                {isManufacturersCardOpen && (
                  <ManufacturerWarrantyContent
                    manufacturer={manufacturerWarranty}
                    vehicleMake={make}
                    cardId={cardId}
                    cardPosition={cardPosition}
                  />
                )}
              </SlideDown>
            </>
          </Intersector>
        )}
      </WarrantiesCardContentContainer>
    </WarrantiesCardWrapper>
  );
};

export default WarrantiesCard;
